import { useState } from 'react';
import { CaretDown, CaretUp, Target, CalendarBlank, ListChecks, Lightbulb, ArrowRight, FilePdf, EnvelopeSimple, Check } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import type { GoalPlan } from '@researcher/shared';
import type { PdfSection } from '../../utils/brandedPdf';
import { useAuth } from '../../contexts/AuthContext';
import { apiUrl } from '../../config/api';

interface GoalPlanCardProps {
  goalId: string;
  goalDescription: string;
  plan: GoalPlan;
}

export default function GoalPlanCard({ goalId, goalDescription, plan }: GoalPlanCardProps) {
  const [expanded, setExpanded] = useState(true);
  const [emailed, setEmailed] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const buildPdfSections = (): PdfSection[] => {
    const sections: PdfSection[] = [];

    sections.push({
      title: 'Overview',
      items: [{ type: 'text', text: plan.summary }],
    });

    if (plan.weeklySchedule.length > 0) {
      sections.push({
        title: 'Weekly Schedule',
        items: plan.weeklySchedule.map(step => ({
          type: 'bullet' as const,
          text: `${step.day}: ${step.activities.join(', ')}`,
        })),
      });
    }

    if (plan.actionItems.length > 0) {
      sections.push({
        title: 'Action Items',
        items: plan.actionItems.map((item, i) => ({
          type: 'numbered' as const,
          number: i + 1,
          text: `${item.action} (${item.timeframe})`,
          subtext: item.rationale,
        })),
      });
    }

    if (plan.tips.length > 0) {
      sections.push({
        title: 'Tips',
        items: plan.tips.map(tip => ({
          type: 'bullet' as const,
          text: `${tip.category}: ${tip.tip}`,
        })),
      });
    }

    return sections;
  };

  const handleExportPDF = async () => {
    const { generateBrandedPdf } = await import('../../utils/brandedPdf');
    await generateBrandedPdf({
      title: goalDescription,
      subtitle: `Action Plan: ${goalDescription}`,
      sections: buildPdfSections(),
      filename: `romy-plan-${goalId.slice(0, 8)}.pdf`,
    });
  };

  const handleEmail = async () => {
    if (!currentUser) return;
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(apiUrl('/email/send-document'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type: 'goal_plan', goalId }),
      });
      if (res.ok) {
        setEmailed(true);
        setTimeout(() => setEmailed(false), 3000);
      }
    } catch { /* silently fail */ }
  };

  return (
    <div className="mt-3 rounded-xl border border-pink-200 dark:border-pink-800 bg-orange-50 dark:bg-gray-800 overflow-hidden not-prose">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-orange-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Target size={18} weight="duotone" className="text-pink-500" />
          <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
            Plan: {goalDescription}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-pink-600 dark:text-pink-400 font-medium">
            {expanded ? 'Hide' : 'View'}
          </span>
          {expanded
            ? <CaretUp size={14} className="text-pink-500" />
            : <CaretDown size={14} className="text-pink-500" />
          }
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-pink-100 dark:border-pink-900/40">
          {/* Summary */}
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 leading-relaxed">
            {plan.summary}
          </p>

          {/* Weekly Schedule */}
          {plan.weeklySchedule.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 mb-2">
                <CalendarBlank size={14} className="text-pink-500" />
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Weekly Schedule
                </h4>
              </div>
              <div className="space-y-3">
                {plan.weeklySchedule.map((step, i) => (
                  <div key={step.id || i} className="text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{step.day}</span>
                    <p className="text-gray-600 dark:text-gray-400 mt-0.5">{step.activities.join(', ')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Items */}
          {plan.actionItems.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 mb-2">
                <ListChecks size={14} className="text-pink-500" />
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Action Items
                </h4>
              </div>
              <div className="space-y-2">
                {plan.actionItems.map((item, i) => (
                  <div key={item.id || i} className="text-sm">
                    <div className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-pink-100 dark:bg-pink-900/40 text-pink-600 dark:text-pink-400 text-xs font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <div>
                        <span className="font-medium text-gray-800 dark:text-gray-200">{item.action}</span>
                        <span className="ml-1.5 text-xs text-pink-600 dark:text-pink-400 font-medium">{item.timeframe}</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.rationale}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tips */}
          {plan.tips.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center gap-1.5 mb-2">
                <Lightbulb size={14} className="text-amber-500" />
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                  Tips
                </h4>
              </div>
              <div className="space-y-3">
                {plan.tips.map((tip, i) => (
                  <div key={i} className="text-sm">
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded">{tip.category}</span>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">{tip.tip}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-pink-100 dark:border-pink-900/40">
            <button
              onClick={() => navigate(`/health/goals/${goalId}`)}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-pink-600 dark:text-pink-400 bg-white dark:bg-gray-700 border border-pink-200 dark:border-pink-700 rounded-lg hover:bg-pink-50 dark:hover:bg-gray-600 transition-colors"
            >
              View Goal <ArrowRight size={12} />
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <FilePdf size={14} />
              PDF
            </button>
            {currentUser && (
              <button
                onClick={handleEmail}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {emailed ? <Check size={14} className="text-emerald-500" /> : <EnvelopeSimple size={14} />}
                {emailed ? 'Sent!' : 'Email'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
